import {
	CountrySchema,
	DateOfBirthSchema,
	useCountries,
	useIntl,
	type UserAMLQuestionnaireRequirementConfig
} from '@capsicum/common';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSetAtom } from 'jotai';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { amlQuestionnaireReducerAtom } from '../../data-access';

type PersonalInformationFormType = z.infer<ReturnType<typeof usePersonalInformationFormSchema>>;

const usePersonalInformationFormSchema = () => {
	const { $t } = useIntl();

	return z.object({
		birthCity: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.birthCity'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.birthCity'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		birthCountry: CountrySchema,
		birthDate: DateOfBirthSchema({
			$t,
			checkAgeOfMajority: true
		}),
		firstName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.firstName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.firstName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			),
		lastName: z
			.string()
			.min(
				2,
				$t('requirements.common.form.errors.length', {
					field: $t('global.lastName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.short')
				})
			)
			.max(
				255,
				$t('requirements.common.form.errors.length', {
					field: $t('global.lastName'),
					max: 255,
					min: 2,
					value: $t('requirements.common.form.errors.long')
				})
			)
	});
};

export const useAmlPersonalInformation = (prefilledData: UserAMLQuestionnaireRequirementConfig['prefill']) => {
	const { getCountryBy } = useCountries();
	const dispatch = useSetAtom(amlQuestionnaireReducerAtom);

	const defaultValues = {
		birthCity: '',
		birthCountry: getCountryBy(undefined, 'iso2'),
		birthDate: prefilledData.user.birthDate || '',
		firstName: prefilledData.user.firstName || '',
		lastName: prefilledData.user.lastName || ''
	};

	const personalInformationForm = useForm<PersonalInformationFormType>({
		defaultValues,
		delayError: 200,
		mode: 'onChange',
		resolver: zodResolver(usePersonalInformationFormSchema())
	});

	const formValues = personalInformationForm.watch();
	const isValid = personalInformationForm.formState.isValid;

	const previousValuesRef = useRef(formValues);
	const previousValidityRef = useRef(isValid);

	useEffect(() => {
		const hasFormValuesChanged = JSON.stringify(previousValuesRef.current) !== JSON.stringify(formValues);
		const hasValidityChanged = previousValidityRef.current !== isValid;

		if (hasFormValuesChanged || hasValidityChanged) {
			previousValuesRef.current = formValues;
			previousValidityRef.current = isValid;

			dispatch({ isValid, stepId: 'PersonalInformation', type: 'UPDATE_STEP_VALIDITY' });
			dispatch({
				data: { ...formValues, birthCountry: formValues.birthCountry?.iso2 },
				type: 'UPDATE_FORM_DATA'
			});
		}
	}, [formValues, isValid]);

	return {
		personalInformationForm
	};
};
